import * as styles from "./LandingPage.module.scss";

import { Head as DefaultHead } from "@dogstrust/dt-web-core-lib-front-end/src/components/Head/Head";
import SSRSuspense from "@dogstrust/dt-web-core-lib-front-end/src/components/SSRSuspense/SSRSuspense";
import SEO from "@dogstrust/dt-web-core-lib-front-end/src/components/seo";
import IntroToDisplay from "@dogstrust/dt-web-core-lib-front-end/src/containers/introBlocks/IntroToDisplay";
import type { IntroType } from "@dogstrust/dt-web-core-lib-front-end/src/containers/introBlocks/IntroToDisplay";
import { ParagraphSections } from "@dogstrust/dt-web-core-lib-front-end/src/containers/paragraphs/ParagraphSections/ParagraphSections";
import { usePageManager } from "@dogstrust/dt-web-core-lib-front-end/src/context";
import { Layout } from "@dogstrust/uk/components/Layout/Layout";

import React, { lazy, useEffect, useMemo, useState } from "react";

import { useClientSide } from "@dogstrust/dt-web-core-lib-front-end/src/hooks/useClientSide";
import { getSeoProps } from "@dogstrust/dt-web-core-lib-front-end/src/utils/seo";
import { graphql } from "gatsby";

const FilterPanel = lazy(
	() =>
		import(
			"@dogstrust/dt-web-core-lib-front-end/src/containers/filters/FilterPanel"
		),
);

interface Props {
	data: Queries.LandingPageQuery;
}

const LandingPage: React.FC<Props> = ({
	data: {
		nodeLandingPage: {
			title,
			breadcrumbs,
			field_pre_heading,
			field_intro_text,
			relationships: {
				field_section_to_display,
				field_intro_type,
				field_images,
			},
		},
		allNodeCentre: { nodes: allCentres },
	},
}) => {
	const { setContentWidth, deregisterPage, usePagination } = usePageManager();
	const [imageIndex, setImageIndex] = useState("0");

	// biome-ignore lint/correctness/useExhaustiveDependencies: We only want this to run once
	useEffect(() => {
		usePagination(false);
		setContentWidth("DEFAULT");

		return () => {
			deregisterPage();
		};
	}, []);

	// The below hook ensures that the image they see in the intro changes every page load.
	// biome-ignore lint/correctness/useExhaustiveDependencies: We don't want imageIndex to trigger a re-render
	useEffect(() => {
		let oldImageIndex = Number.parseInt(
			localStorage.getItem("intro-landing-image"),
		);
		if (Number.isNaN(oldImageIndex))
			oldImageIndex = Number.parseInt(imageIndex);
		const nextImage = ((oldImageIndex + 1) % field_images.length).toString();
		setImageIndex(nextImage);
		localStorage.setItem("intro-landing-image", nextImage);
	}, [field_images]);

	const image = useMemo(
		() => field_images[Number.parseInt(imageIndex)],
		[imageIndex, field_images],
	);

	const isClientSide = useClientSide();

	return (
		<>
			<Layout breadcrumbs={breadcrumbs ? [...breadcrumbs] : []}>
				<div className={styles.contentarea}>
					<IntroToDisplay
						key={imageIndex}
						introType={field_intro_type.name as IntroType}
						title={title}
						preHeading={field_pre_heading}
						body={field_intro_text?.processed || field_intro_text?.value}
						imgSrc={image?.field_media_image?.uri}
						imgAlt={image?.field_media_image?.alt}
						firstSection={
							field_section_to_display?.length > 0 &&
							field_section_to_display[0]
						}
					/>
					{field_section_to_display?.map((section, i) => (
						<ParagraphSections
							key={"id" in section ? section.id : i}
							data={section}
							thisSectionKey={i}
						/>
					))}
				</div>
			</Layout>
			<SSRSuspense>
				<FilterPanel
					allCentres={allCentres as Queries.RehomingCentreDataFragment[]}
					isListingPage={false}
					shouldMaintainFilters={
						isClientSide && window.location.href.includes("rehoming/dogs")
					}
				/>
			</SSRSuspense>
		</>
	);
};

export const Head: React.FC<Props> = ({
	data: {
		nodeLandingPage: {
			title,
			field_hreflang_url,
			field_intro_text,
			field_meta_tags,
			path: { alias },
		},
	},
}) => {
	const description = field_intro_text?.processed || field_intro_text?.value;
	const maxLength = 150; // maximum number of characters to extract
	let trimmedMetaDescription = "";
	if (description) {
		const metaDescription = description.replace(/(<([^>]+)>)/gi, "");
		//trim the description to the maximum length
		trimmedMetaDescription = metaDescription.substr(0, maxLength);
		//re-trim if we are in the middle of a word
		if (metaDescription.length > maxLength) {
			trimmedMetaDescription = trimmedMetaDescription.substr(
				0,
				Math.min(
					trimmedMetaDescription.length,
					trimmedMetaDescription.lastIndexOf(" "),
				),
			);
		}
	}
	return (
		<DefaultHead pageUrl={alias} hrefLangUrl={field_hreflang_url}>
			<SEO
				{...getSeoProps(field_meta_tags, {
					title: `${title} | Dogs Trust`,
					description: trimmedMetaDescription,
					canonical_url: process.env.GATSBY_DOMAIN_NAME + alias,
					og_type: "article",
					og_url: process.env.GATSBY_DOMAIN_NAME + alias,
					og_title: `${title} | Dogs Trust`,
					og_description: trimmedMetaDescription,
					twitter_cards_type: "summary",
					twitter_cards_description: trimmedMetaDescription,
					twitter_cards_site: "@DogsTrust",
					twitter_cards_title: `${title} | Dogs Trust`,
					twitter_cards_page_url: process.env.GATSBY_DOMAIN_NAME + alias,
				})}
			/>
		</DefaultHead>
	);
};
export default LandingPage;

export const query = graphql`
	
    query LandingPage($path: String) {
        nodeLandingPage(path: { alias: { eq: $path } }) {
            __typename
            title
            path {
                alias
            }
            breadcrumbs {
                title
                url
            }
			field_hreflang_url
            field_pre_heading
            field_intro_text {
                processed
                value
            }
            field_is_sub_landing_page
            field_short_link_text
            field_meta_tags {
                title
                description
                abstract
                keywords
                canonical_url
                robots
                og_type
                og_url
                og_title
                og_description
                og_image_url
                twitter_cards_type
                twitter_cards_description
                twitter_cards_site
                twitter_cards_title
                twitter_cards_page_url
                twitter_cards_image
            }
            relationships {
                field_intro_type {
                    name
                }
                field_images {
                    field_media_image {
                        uri
                        alt
                    }
                }
                field_section_to_display {
                    ...ParagraphSectionsData
                }
                field_category_type {
                    name
                }
            }
        }
        allNodeCentre {
            nodes {
                field_longitude
                field_latitude
                title
                relationships {
                    field_centre_code {
                        name
                    }
                }
            }
        }
    }
`;
